import React, { memo, useMemo, useCallback, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import "./product-view-inline.css";
import parse from "html-react-parser";
import {
	SurfaceActionBar,
	SurfaceActionBarButton,
	SurfaceExpandableCard,
	SurfaceNavBarBrandButton,
	SurfaceNavBarButton,
	SurfaceTitle,
} from "../surfaces/SurfacesCoreUI";
import { DummyImage, ImageCarousel, Loader } from "../CoreUI";
import { DEFAULT_SHOP_IMG } from "../../conf";
import {
	ProductViewActionBar,
	ProductViewActionOverlay,
} from "./ProductViewCore";
import usePDP from "../../hooks/usePDP";
import anim from "../../utils/anim";
import { MdClose as CrossIcon } from "react-icons/md";
import genClassName from "../../utils/genClassName";
import useTrack, { eventTypes } from "../../hooks/useTrack";
import getParsedRedirectUrl from "../../utils/getParsedRedirectUrl";
import productEventPropertyMapper from "../../utils/productEventPropertyMapper";
import windowRedirect from "../../utils/windowRedirect";
import { Textfit } from "react-textfit";
import { PRODUCT_CLICK_TARGET } from "../../event_conf";
import getImgUrl from "../../utils/getImgUrl";
import isArray from "../../utils/isArray";
import useData from "../../hooks/useData";

const INLINE_PDP_HEIGHT = 374;
const INLINE_MINIMAL_PDP_HEIGHT = 300;
const ProductViewInline = memo(
	({
		loading = false,
		product,
		brand,
		actionBarLoading = false,
		handleClose,
		showActionOverlay,
		handleActionOverlayOpen,
		handleActionOverlayClose,
		minimal = true,
		...props
	}) => {
		const innerRef = useRef();
		const {
			name,
			discounted_price_str: discountedPrice,
			price_str: price,
			pdp_description: description,
			// description = DUMMY_DESCRIPTION,
		} = useMemo(() => product || {}, [product]);
		const {
			PDPInfo,
			setPDPInfo,
			setHoveringPDP,
			hoveringPDP,
			hoveringProduct,
			targetProductsScrolling,
		} = usePDP();

		const handleMouseEnter = useCallback(
			() => setHoveringPDP(true),
			[setHoveringPDP]
		);
		const handleMouseLeave = useCallback(
			() => setHoveringPDP(false),
			[setHoveringPDP]
		);

		const { feedHasUpsell } = useData();

		const track = useTrack();
		const defaultEventProps = useMemo(() => {
			const { product, brand, brandIdx, idx } = PDPInfo;
			const eventProps = productEventPropertyMapper({
				brand,
				brandIdx,
				idx,
				product,
				feedHasUpsell,
			});

			return eventProps;
		}, [PDPInfo, feedHasUpsell]);

		const handleProductRedirect = useCallback(() => {
			const { product, brand } = PDPInfo;
			const { masked_url: productUrl } = product;
			const redirectUrl = getParsedRedirectUrl({
				brand,
				redirectUrl: productUrl,
			});

			track(eventTypes.CLICK, {
				...defaultEventProps,
				from_pdp: true,
				target: PRODUCT_CLICK_TARGET.PDP,
			});
			windowRedirect({ url: redirectUrl });
		}, [PDPInfo, track, defaultEventProps]);

		const handleDescriptionExpand = useCallback(() => {
			track(
				eventTypes.WIDGET_PDP_PRODUCT_DETAIL_CLICK,
				defaultEventProps
			);
		}, [defaultEventProps, track]);

		// const [yAnim, setYAnim] = useState([]);

		// useEffect(() => {
		// 	if (!PDPInfo) {
		// 		return setHoveringPDP(false);
		// 	}
		// 	const productRect =
		// 		PDPInfo?.productRef?.current?.getBoundingClientRect();
		// 	const innerRect = innerRef.current?.getBoundingClientRect();
		// 	if (!productRect || !innerRect) return;

		// 	let showAbove = false;
		// 	const productInfo = productRect;
		// 	const widgetInfo = widgetRef?.current?.getBoundingClientRect();
		// 	if (!productInfo || !widgetInfo) {
		// 		showAbove = false;
		// 	} else if (
		// 		productInfo.bottom + INLINE_PDP_HEIGHT + 30 <
		// 		widgetInfo.bottom
		// 	) {
		// 		showAbove = false;
		// 	} else showAbove = true;

		// 	const aboveY = productRect.top - innerRect.height - 5;
		// 	const belowY = productRect.top + productRect.height + 5;

		// 	setYAnim(showAbove ? [aboveY - 20, aboveY] : [belowY + 20, belowY]);
		// }, [widgetRef, PDPInfo, setHoveringPDP]);

		const closingTimerRef = useRef(null);

		useEffect(() => {
			if (hoveringProduct || hoveringPDP) {
				clearTimeout(closingTimerRef.current);
				return;
			}
			closingTimerRef.current = setTimeout(() => setPDPInfo(false), 200);
		}, [hoveringPDP, hoveringProduct, setPDPInfo]);

		return (
			// <motion.section className="product-view-inline-card-overlay">
			<motion.section
				className={genClassName({
					base: "product-view-inline-card-container",
					conditionals: {
						"product-view-inline-card-container-transition":
							targetProductsScrolling,
					},
				})}
				onMouseEnter={handleMouseEnter}
				onMouseLeave={handleMouseLeave}
				animate={{
					y: 20,
				}}
				transition={{ duration: 0.2, ease: "easeOut", delay: 0 }}
				ref={innerRef}
			>
				<motion.section
					className={genClassName({
						base: "product-view-inline-card",
						conditionals: {
							"product-view-inline-card-minimal": minimal,
						},
					})}
					style={{
						height: `${
							minimal
								? INLINE_MINIMAL_PDP_HEIGHT
								: INLINE_PDP_HEIGHT
						}px`,
					}}
					variants={anim.pdp}
					initial="initial"
					animate="animate"
					exit="exit"
				>
					{loading || !product ? (
						<Loader center />
					) : (
						<>
							{!minimal && (
								<section className="product-view-inline-card-header">
									<SurfaceNavBarBrandButton
										brandInfo={brand}
										text={brand.name}
									/>
									<SurfaceNavBarButton onClick={handleClose}>
										<CrossIcon />
									</SurfaceNavBarButton>
								</section>
							)}

							<section className="product-view-inline-card-body">
								<ImageCarousel
									imgArray={product?.supplementary_images}
									imgUrl={
										getImgUrl(product, 200, 200) ||
										DEFAULT_SHOP_IMG
									}
								/>
								{minimal &&
									isArray(brand.products) &&
									brand.products.map((product) => (
										<DummyImage
											key={product.remote_id}
											src={getImgUrl(product, 200, 200)}
										/>
									))}
								<section className="product-view-inline-card-body-information">
									<SurfaceTitle>
										<Textfit
											mode="multi"
											max={28}
											className={genClassName({
												base: "product-view-inline-card-body-title",
												conditionals: {
													"product-view-inline-card-body-title-large":
														name?.length > 20,
												},
											})}
										>
											{name}
										</Textfit>
									</SurfaceTitle>
									<h3>${discountedPrice || price}</h3>
									{description && (
										<SurfaceExpandableCard
											onExpand={handleDescriptionExpand}
										>
											{parse(description)}
										</SurfaceExpandableCard>
									)}
								</section>
							</section>

							<SurfaceActionBar>
								{minimal ? (
									<SurfaceActionBarButton
										type="dark"
										className="product-view-inline-minimal-action-bar-btn"
										onClick={handleProductRedirect}
									>
										Buy Now
									</SurfaceActionBarButton>
								) : (
									<ProductViewActionBar
										showActionOverlay={showActionOverlay}
										handleActionOverlay={
											handleActionOverlayOpen
										}
										loading={actionBarLoading}
										{...props}
									/>
								)}
							</SurfaceActionBar>
							{!minimal && (
								<ProductViewActionOverlay
									open={showActionOverlay}
									product={product}
									handleClose={handleActionOverlayClose}
									{...props}
								/>
							)}
						</>
					)}
				</motion.section>
			</motion.section>
			// </motion.section>
		);
	}
);
export default ProductViewInline;
